/** Public map links deliberately have no route, location-share or account fields. */
export const MAP_VIEW_PARAM = "mapView";
export const MAX_MAP_VIEW_BYTES = 1800;
export type PublicMapFeature = { kind: "trail" | "lift" | "place" | "landmark"; id: string };
export type PublicMapCamera = { lng: number; lat: number; zoom: number; bearing: number; pitch: number };
export type PublicMapView = {
  version: 1;
  region: string;
  entry: string;
  sport: "ski" | "mtb";
  sector: string;
  dimension: "2d" | "3d";
  relief: "dramatic" | "natural";
  camera: PublicMapCamera;
  feature?: PublicMapFeature;
};
export type MapViewContext = { region: string; entry: string; sport: "ski" | "mtb"; bbox: readonly number[]; sectors: readonly string[]; maxPitch: number };

export function isMapSlug(value: unknown): value is string {
  return typeof value === "string" && value.length <= 100 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);
}
function record(value: unknown): value is Record<string, unknown> { return value !== null && typeof value === "object" && !Array.isArray(value); }
function keys(value: Record<string, unknown>, allowed: string[]) { return Object.keys(value).every(key => allowed.includes(key)); }
function between(value: unknown, min: number, max: number): value is number { return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max; }
export function parsePublicMapFeature(value: unknown): PublicMapFeature | null {
  if (!record(value) || !keys(value, ["kind", "id"]) || !["trail", "lift", "place", "landmark"].includes(String(value.kind)) || typeof value.id !== "string" || !/^[a-zA-Z0-9][a-zA-Z0-9:_-]{0,159}$/.test(value.id)) return null;
  return { kind: value.kind as PublicMapFeature["kind"], id: value.id };
}

export function parsePublicMapView(value: unknown): PublicMapView | null {
  if (!record(value) || !keys(value, ["version", "region", "entry", "sport", "sector", "dimension", "relief", "camera", "feature"]) || value.version !== 1 || !isMapSlug(value.region) || !isMapSlug(value.entry) || !isMapSlug(value.sector) || (value.sport !== "ski" && value.sport !== "mtb") || (value.dimension !== "2d" && value.dimension !== "3d") || (value.relief !== "dramatic" && value.relief !== "natural")) return null;
  const c = value.camera;
  if (!record(c) || !keys(c, ["lng", "lat", "zoom", "bearing", "pitch"]) || !between(c.lng, -180, 180) || !between(c.lat, -85, 85) || !between(c.zoom, 8, 18) || !between(c.bearing, -180, 180) || !between(c.pitch, 0, 65) || (value.dimension === "2d" && c.pitch !== 0)) return null;
  const feature = value.feature === undefined ? undefined : parsePublicMapFeature(value.feature);
  if (feature === null) return null;
  return { version: 1, region: value.region, entry: value.entry, sport: value.sport, sector: value.sector, dimension: value.dimension, relief: value.relief, camera: { lng: c.lng, lat: c.lat, zoom: c.zoom, bearing: c.bearing, pitch: c.pitch }, ...(feature ? { feature } : {}) };
}

/** Check against the loaded public region, never use a link to fetch an arbitrary region. */
export function validateMapViewContext(view: PublicMapView, context: MapViewContext): boolean {
  if (view.region !== context.region || view.entry !== context.entry || view.sport !== context.sport || (view.sector !== "whole" && !context.sectors.includes(view.sector)) || view.camera.pitch > context.maxPitch) return false;
  const [west, south, east, north] = context.bbox;
  if (context.bbox.length !== 4 || !context.bbox.every(Number.isFinite) || west > east || south > north) return false;
  // Permit nearby valley framing while refusing remote or extreme camera targets.
  const dx = Math.max(0.08, (east - west) * 0.25), dy = Math.max(0.08, (north - south) * 0.25);
  return view.camera.lng >= west - dx && view.camera.lng <= east + dx && view.camera.lat >= south - dy && view.camera.lat <= north + dy;
}

export function decodePublicMapView(raw: string | null): PublicMapView | null {
  if (!raw || new TextEncoder().encode(raw).length > MAX_MAP_VIEW_BYTES) return null;
  try { return parsePublicMapView(JSON.parse(raw)); } catch { return null; }
}

export function publicMapPath(input: PublicMapView): string | null {
  const view = parsePublicMapView(input);
  if (!view) return null;
  const raw = JSON.stringify(view);
  if (new TextEncoder().encode(raw).length > MAX_MAP_VIEW_BYTES) return null;
  const query = new URLSearchParams({ sport: view.sport === "ski" ? "piste" : "mtb", tab: "map", [MAP_VIEW_PARAM]: raw });
  return `/conditions/${view.entry}?${query}#location-sections`;
}

export function publicMapUrl(origin: string, view: PublicMapView): string | null {
  const path = publicMapPath(view);
  if (!path) return null;
  try {
    const base = new URL(origin);
    if (base.username || base.password || base.pathname !== "/" || base.search || base.hash || (base.protocol !== "https:" && !(base.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(base.hostname)))) return null;
    return new URL(path, base.origin).href;
  } catch { return null; }
}

/** Native cancellation is final. Denied or unsupported sharing falls back to copy. */
export async function deliverPublicMapLink(url: string, method: "copy" | "share", port: { share?: (data: { title: string; url: string }) => Promise<void>; copy?: (text: string) => Promise<void> }): Promise<"shared" | "copied" | "cancelled" | "manual"> {
  if (method === "share" && port.share) {
    try { await port.share({ title: "PeakWave public mountain map", url }); return "shared"; }
    catch (error) { if (error && typeof error === "object" && "name" in error && error.name === "AbortError") return "cancelled"; }
  }
  if (port.copy) { try { await port.copy(url); return "copied"; } catch { /* The visible link is the final fallback. */ } }
  return "manual";
}
